import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66695 epic (dl-57695, Testmo folder 93168, part 9 of 9 — Hierarchy CF
 * completion and the Numeric custom-field filter, standalone). Real, persisted `stylesData` rows
 * only — never `apiTransform` injection (DL-64371). Additive: appended on top of every existing
 * generated + sibling-epic fixture row. Ids 99126+ (part 8's `stylesCfEnumHierarchyFixtureData.ts`
 * reserved and used 99118-99125).
 *
 * Reuses the EXISTING CF 708 "Target Weight (g)" definition (`stylesCustomFieldDefinitions.ts`,
 * `type: 4` Numeric, `allowedDecimals: 0`) rather than adding a new one — the same field DL-66657
 * (part 6) and DL-66664/66667/66668 (part 6/7) already exercise for the CF-combination/URL-
 * persistence stories, filtered here via its own `cf_708_number` param in total isolation (no
 * standard-filter combination).
 *
 * One number line, one row per value, so a single fixture set discriminates every hazard the DL-
 * 66695 epic calls out without duplicating rows per story:
 *
 * | Row | Value | Role |
 * |---|---|---|
 * | `zero` | 0 | zero-falsy probe — a `max=0`/`min=0` bound must not be silently dropped as "unset" |
 * | `four` | 4 | excluded just below the range/min-only lower bounds |
 * | `five` | 5 | range lower boundary (inclusive) |
 * | `nine` | 9 | **the single-digit-inside-a-two-digit-range probe** — under a lexicographic string
 * |   |   | comparison, `'9' >= '10'` is true (wrongly included when filtering `min=10`) and
 * |   |   | `'9' <= '10'` is false (wrongly EXCLUDED when filtering `max=10`); asserting its
 * |   |   | presence/absence on both sides of that boundary is the discriminating proof |
 * | `ten` | 10 | the equality target (`min=max=10`) and the min/max boundary used with `nine` above |
 * | `fifteen` | 15 | interior value, mid-range |
 * | `twenty` | 20 | range upper boundary (inclusive) |
 * | `twentyOne` | 21 | excluded just above the range/max-only upper bounds |
 */

let nextFixtureId = 99126;
const nextId = (): number => nextFixtureId++;

const baseRow = (overrides: Partial<StyleRow> & { name: string; customId: string }): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-04-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

export const CF_NUMERIC_CUSTOM_IDS = {
  zero: 'CFNUM-01',
  four: 'CFNUM-02',
  five: 'CFNUM-03',
  nine: 'CFNUM-04',
  ten: 'CFNUM-05',
  fifteen: 'CFNUM-06',
  twenty: 'CFNUM-07',
  twentyOne: 'CFNUM-08',
};

const numericField = (value: number): CustomFieldRowEntry[] => [
  { id: 708, type: 'numeric', value: { id: 0, value: String(value) } },
];

export const buildCfNumericFixtureStyles = (): StyleRow[] => [
  baseRow({
    name: 'CF Numeric Zero',
    customId: CF_NUMERIC_CUSTOM_IDS.zero,
    customFields: numericField(0),
  }),
  baseRow({
    name: 'CF Numeric Four',
    customId: CF_NUMERIC_CUSTOM_IDS.four,
    customFields: numericField(4),
  }),
  baseRow({
    name: 'CF Numeric Five',
    customId: CF_NUMERIC_CUSTOM_IDS.five,
    customFields: numericField(5),
  }),
  baseRow({
    name: 'CF Numeric Nine',
    customId: CF_NUMERIC_CUSTOM_IDS.nine,
    customFields: numericField(9),
  }),
  baseRow({
    name: 'CF Numeric Ten',
    customId: CF_NUMERIC_CUSTOM_IDS.ten,
    customFields: numericField(10),
  }),
  baseRow({
    name: 'CF Numeric Fifteen',
    customId: CF_NUMERIC_CUSTOM_IDS.fifteen,
    customFields: numericField(15),
  }),
  baseRow({
    name: 'CF Numeric Twenty',
    customId: CF_NUMERIC_CUSTOM_IDS.twenty,
    customFields: numericField(20),
  }),
  baseRow({
    name: 'CF Numeric TwentyOne',
    customId: CF_NUMERIC_CUSTOM_IDS.twentyOne,
    customFields: numericField(21),
  }),
];
